import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Mountain, Loader2, Layers3 } from "lucide-react";
import { api } from "../lib/apiClient";
import { normalizeFloors } from "../lib/blueprint";
import { useFakeProgress } from "../lib/useFakeProgress";
import { toast } from "sonner";

const SLOPES = ["Flat", "Downhill", "Uphill", "Cross-slope"];

export default function SiteDesigner({ open, onOpenChange, onApply, city }) {
  const [prompt, setPrompt] = useState("");
  const [slope, setSlope] = useState("Downhill");
  const [grade, setGrade] = useState(15);
  const [floors, setFloors] = useState("2");
  const [basement, setBasement] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { progress, elapsed, est, start, done, reset } = useFakeProgress();

  const generate = async () => {
    setLoading(true); setResult(null); start(55);
    try {
      const res = await api.post("/ai/site-design", {
        prompt, city, floors: Number(floors), slope_type: slope, slope_pct: Number(grade), basement,
      });
      setResult(res.data);
      done();
      toast.success("Site-fitted design ready");
    } catch (e) { toast.error(e.response?.data?.detail || "Design failed"); reset(); }
    finally { setLoading(false); }
  };

  const apply = () => {
    const flrs = normalizeFloors(result);
    onApply(flrs, result, { slope_type: slope, slope_pct: Number(grade) });
    onOpenChange(false);
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#161B22] border-[#30363D] text-[#F0F6FC] max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <Mountain className="w-5 h-5 text-[#F43F5E]" /> Site & Slope Designer
          </DialogTitle>
          <DialogDescription className="text-[#8B949E]">Describe your lot's terrain and let the AI engineer a multi-storey house that fits the slope.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-[#8B949E]">Lot Slope</Label>
              <Select value={slope} onValueChange={setSlope}>
                <SelectTrigger data-testid="slope-type-select" className="bg-[#0D1117] border-[#30363D]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                  {SLOPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#8B949E]">Grade (%)</Label>
              <Input type="number" min="0" value={grade} onChange={(e) => setGrade(e.target.value)} className="bg-[#0D1117] border-[#30363D]" data-testid="slope-grade-input" />
            </div>
            <div>
              <Label className="text-xs text-[#8B949E]">Floors</Label>
              <Select value={floors} onValueChange={setFloors}>
                <SelectTrigger data-testid="floors-count-select" className="bg-[#0D1117] border-[#30363D]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                  {["1", "2", "3", "4"].map((s) => <SelectItem key={s} value={s}>{s} storey</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Optional: e.g. hillside modern home with a walkout lower level and 3 bedrooms"
            className="bg-[#0D1117] border-[#30363D] min-h-[70px] font-mono-cad text-sm" data-testid="site-prompt-input" />
          <button type="button" onClick={() => setBasement((b) => !b)} disabled={slope === "Flat"}
            data-testid="basement-toggle"
            className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${slope === "Flat" ? "border-[#21262D] opacity-50 cursor-not-allowed" : basement ? "border-[#34D399] bg-[#34D399]/10" : "border-[#30363D] hover:border-[#34D399]"}`}>
            <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${basement ? "bg-[#34D399] border-[#34D399] text-[#0D1117]" : "border-[#6E7681]"}`}>{basement ? "✓" : ""}</span>
            <span className="flex-1 text-sm">
              <span className="text-[#F0F6FC] font-medium">Walk-out basement</span>
              <span className="block text-xs text-[#8B949E]">Turn the lowest level into a daylit walk-out with an exterior door &amp; downhill windows{slope === "Flat" ? " (needs a slope)" : ""}.</span>
            </span>
          </button>
          <Button data-testid="site-generate-btn" onClick={generate} disabled={loading} className="w-full bg-[#F43F5E] hover:bg-[#e11d48] text-white font-semibold">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Engineering to site…</> : <><Layers3 className="w-4 h-4 mr-2" /> Design to Site</>}
          </Button>

          {loading && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2 bg-[#0D1117]" />
              <div className="flex justify-between text-xs text-[#8B949E] font-mono-cad">
                <span>Fitting {floors}-storey house to {slope.toLowerCase()} lot… {Math.round(progress)}%</span>
                <span>{elapsed.toFixed(0)}s / ~{est}s</span>
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-[#30363D] bg-[#0D1117] p-4 space-y-2 animate-fade-up" data-testid="site-result">
              <h4 className="font-heading font-semibold">{result.name}</h4>
              <p className="text-xs text-[#8B949E] leading-relaxed">{result.specs}</p>
              {result.site_notes && (
                <p className="text-xs text-[#F43F5E] leading-relaxed border-l-2 border-[#F43F5E] pl-2">{result.site_notes}</p>
              )}
              <div className="flex gap-3 text-xs font-mono-cad text-[#8B949E]">
                <span>{result.floors?.length || 0} floors</span>
                <span>·</span>
                <span>{result.bedrooms || 0} bed</span>
                <span>·</span>
                <span>{result.total_area_sqft || 0} ft²</span>
              </div>
              <Button data-testid="site-apply-btn" onClick={apply} className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold">
                Load {result.floors?.length || 0} Floor(s) onto Canvas
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
